import {
  AddRounded as AddIcon,
  CloseRounded as CloseIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import * as dates from "common/dates";
import * as logger from "common/logger";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import Notes from "components/common/Notes";
import * as config from "config";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import * as notesService from "services/notesService";

const useNotifyError = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const notifyError = (message) => {
    enqueueSnackbar(message, {
      variant: "error",
      autoHideDuration: 10_000,
      action: (messageId) => (
        <Button
          color={"error"}
          size={"small"}
          onClick={() => closeSnackbar(messageId)}
        >
          {"Dismiss"}
        </Button>
      ),
    });
  };

  return { notifyError };
};

const Controls = ({ addNote, isAddingNote, label, hideIcon }) => {
  return (
    <Button
      startIcon={hideIcon ? null : <AddIcon />}
      sx={{
        borderRadius: 15,
      }}
      isLoading={isAddingNote}
      onClick={addNote}
      size={"small"}
    >
      {label ?? "Add Note"}
    </Button>
  );
};

const BlankSlate = ({ addNote, isAddingNote }) => (
  <Paper elevation={0} sx={{ padding: "3rem", height: "calc(100% - 0.5rem)" }}>
    <Stack
      alignItems={"center"}
      justifyContent={"center"}
      gap={2}
      height={"100%"}
    >
      <Typography color={"gray"} textAlign={"center"}>
        {"Nobody's added any meeting notes yet"}
      </Typography>
      <Controls
        addNote={addNote}
        isAddingNote={isAddingNote}
        label={"Add the first note"}
        hideIcon={true}
      />
    </Stack>
  </Paper>
);

const Note = ({
  submissionId,
  noteId,
  deleteNote,
  setIsUpdating,
  setIsEditing,
}) => {
  const { notifyError } = useNotifyError();

  const {
    currentData: note,
    isFetching,
    isError,
    refetch,
  } = notesService.useRetrieveNoteQuery({
    submissionId,
    noteId,
  });

  const [
    _updateNote,
    { isLoading: isUpdating },
  ] = notesService.useUpdateNoteMutation();

  const updateNote = async (content, title) => {
    try {
      setIsUpdating(true);
      const response = await _updateNote({
        submissionId,
        noteId,
        title,
        content,
      });
      if (response.error) {
        throw new Error(`Could not update note: ${response}`);
      }
      refetch();
    } catch (e) {
      logger.exception(e);
      notifyError(
        `Error updating note. Please retry or contact ${config.SUPPORT_EMAIL}.`
      );
      throw new Error("Could not update note");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Notes
      value={note?.content ?? ""}
      title={note?.title ?? ""}
      isTitleEditable={true}
      update={updateNote}
      loading={isFetching || isUpdating || isError}
      onDelete={() => deleteNote({ noteId })}
      onEnterEditMode={() => setIsEditing(true)}
      onExitEditMode={() => setIsEditing(false)}
    />
  );
};

const Content = ({
  isOpen,
  onClose,
  closeDisabled,
  setIsEditing,
  setIsUpdating,
}) => {
  const { submissionId } = useParams();

  const { notifyError } = useNotifyError();

  const {
    data: _notes,
    isLoading: isLoadingNotes,
    isError: isErrorListingNotes,
    refetch: refetchNotes,
  } = notesService.useListNotesQuery({
    submissionId,
  });

  const [optimisticallyDeleted, setOptimisticallyDeleted] = useState({});

  const [_deleteNote] = notesService.useDeleteNoteMutation();

  const deleteNote = async ({ noteId }) => {
    try {
      setOptimisticallyDeleted((current) => ({ ...current, [noteId]: true }));
      const response = await _deleteNote({
        submissionId,
        noteId,
      });
      if (response.error) {
        logger.error(response.error);
        notifyError(
          `Error deleting note. Please retry or contact ${config.SUPPORT_EMAIL}.`
        );
      }
      await refetchNotes();
    } catch (e) {
      logger.exception(e);
      notifyError(
        `Error deleting note. Please retry or contact ${config.SUPPORT_EMAIL}.`
      );
    } finally {
      setOptimisticallyDeleted((current) => {
        const { [noteId]: _, ...rest } = current;
        return rest;
      });
    }
  };

  const [_createNote] = notesService.useCreateNoteMutation();
  const [isAddingNote, setIsAddingNote] = useState(false);

  const addNote = async () => {
    try {
      setIsAddingNote(true);
      const response = await _createNote({
        submissionId,
        title: dates.dateName(new Date()),
        content: "",
      });
      if (response.error) {
        logger.error(response.error);
        notifyError(
          `Error adding a new note. Please retry or contact ${config.SUPPORT_EMAIL}.`
        );
      } else {
        await refetchNotes();
      }
    } catch (e) {
      logger.exception(e);
      notifyError(
        `Error adding a new note. Please retry or contact ${config.SUPPORT_EMAIL}.`
      );
    } finally {
      setIsAddingNote(false);
    }
  };

  const notes = useMemo(
    () =>
      [...(_notes ?? [])]
        .filter(({ noteId }) => !optimisticallyDeleted[noteId])
        .sort((a, b) => b.createTime?.localeCompare(a.createTime)),
    [_notes, optimisticallyDeleted]
  );

  return (
    <Dialog
      open={isOpen}
      fullScreen
      sx={{ padding: "2rem" }}
      PaperProps={{
        sx: { borderRadius: "0.375rem" },
      }}
      onClose={() => {}}
    >
      <DialogTitle sx={{ padding: "0.5rem 1rem" }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Typography variant={"h6"}>{"Meeting Notes"}</Typography>
            {notes.length > 0 && (
              <Controls addNote={addNote} isAddingNote={isAddingNote} />
            )}
          </Stack>
          <IconButton
            icon={CloseIcon}
            onClick={onClose}
            disabled={closeDisabled}
          />
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ padding: "0.5rem 1rem" }}>
        {isLoadingNotes || isErrorListingNotes ? (
          <BigLoader />
        ) : notes.length === 0 ? (
          <BlankSlate addNote={addNote} isAddingNote={isAddingNote} />
        ) : (
          <Stack gap={1.5}>
            {notes.map(({ noteId }) => (
              <Note
                key={noteId}
                submissionId={submissionId}
                noteId={noteId}
                deleteNote={deleteNote}
                setIsEditing={setIsEditing(noteId)}
                setIsUpdating={setIsUpdating(noteId)}
              />
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MeetingNotes = ({ isOpen, onClose }) => {
  const [isUpdatingByNoteId, setIsUpdatingByNoteId] = useState({});
  const [isEditingByNoteId, setIsEditingByNoteId] = useState({});

  const setIsUpdating = (noteId) => (isUpdating) =>
    setIsUpdatingByNoteId({
      ...isUpdatingByNoteId,
      [noteId]: isUpdating,
    });

  const setIsEditing = (noteId) => (isEditing) =>
    setIsEditingByNoteId({
      ...isEditingByNoteId,
      [noteId]: isEditing,
    });

  const anyNotesEditing = Object.values(isEditingByNoteId).some(
    (isEditing) => isEditing
  );
  const anyNotesUpdating = Object.values(isUpdatingByNoteId).some(
    (isUpdating) => isUpdating
  );

  return (
    <Content
      isOpen={isOpen}
      onClose={onClose}
      closeDisabled={anyNotesEditing || anyNotesUpdating}
      setIsUpdating={setIsUpdating}
      setIsEditing={setIsEditing}
    />
  );
};

export default MeetingNotes;
